"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence, useReducedMotion } from "@/lib/motion";
import { useFormik } from "formik";
import { cn } from "@/lib/utils";
import { fieldTouchedError } from "@/lib/form-helpers";
import { buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, CheckCircle2, Send } from "@/components/icons";
import { TrustBenefitList, brandActionClasses } from "@/components/ui";
import { EyebrowBadge } from "@/components/ui/eyebrow-badge";
import { FormError } from "@/components/ui/form-error";
import {
  STATS,
  CONTACT_SALES_URL,
  FREE_TRIAL_URL,
  FREE_TRIAL_LABEL,
  ROUTES,
} from "@/lib/constants";
import { MAINTENANCE_PATH } from "@/lib/maintenance";
import { ReviewBadges } from "@/components/ui";
import { useUptime } from "@/hooks/use-uptime";
import { useMaintenanceMode } from "@/hooks/use-maintenance-mode";
import { useAffiliateRef } from "@/hooks/use-affiliate-ref";
import {
  heroEmailSchema,
  buildLines,
  containerVariants,
  itemVariants,
  STAGGER_MS,
  PLACEHOLDER_FULL,
  CODE_GREEN,
} from "./data";

export default function HeroSection() {
  const prefersReducedMotion = useReducedMotion();
  const uptime = useUptime();
  const maintenanceMode = useMaintenanceMode();
  const { ref: affiliateRef } = useAffiliateRef();
  const isBuyMode = !!affiliateRef;
  const ref = useRef(null);
  const trialHref = maintenanceMode
    ? MAINTENANCE_PATH
    : isBuyMode
      ? ROUTES.pricing
      : FREE_TRIAL_URL;

  const [username, setUsername] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: heroEmailSchema,
    onSubmit: (values) => {
      const trimmed = values.email.trim();
      if (!trimmed) return;
      setUsername(trimmed);
      setSubmitted(true);
      setFlash(true);
      setTimeout(() => setFlash(false), 800);
    },
  });

  const lines = buildLines(username);
  const [visibleCount, setVisibleCount] = useState(0);
  const [cursorOn, setCursorOn] = useState(true);
  const [flash, setFlash] = useState(false);

  const [placeholderText, setPlaceholderText] = useState("");
  const [phForward, setPhForward] = useState(true);
  const [inputFocused, setInputFocused] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion) {
      setVisibleCount(lines.length);
      return;
    }
    if (visibleCount < lines.length) {
      const t = setTimeout(() => setVisibleCount((v) => v + 1), STAGGER_MS);
      return () => clearTimeout(t);
    }
  }, [visibleCount, lines.length, prefersReducedMotion]);

  useEffect(() => {
    const t = setInterval(() => setCursorOn((v) => !v), 530);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (submitted || inputFocused) return;
    const delay = phForward ? 80 : 40;
    const t = setTimeout(() => {
      if (phForward) {
        if (placeholderText.length < PLACEHOLDER_FULL.length) {
          setPlaceholderText(PLACEHOLDER_FULL.slice(0, placeholderText.length + 1));
        } else {
          setTimeout(() => setPhForward(false), 1800);
        }
      } else {
        if (placeholderText.length > 0) {
          setPlaceholderText(PLACEHOLDER_FULL.slice(0, placeholderText.length - 1));
        } else {
          setTimeout(() => setPhForward(true), 500);
        }
      }
    }, delay);
    return () => clearTimeout(t);
  }, [placeholderText, phForward, submitted, inputFocused]);

  return (
    <section className="relative overflow-hidden pt-32 pb-28 lg:pt-40 lg:pb-36" aria-label="Hero">
      <style>{`
        .hero-cta-btn {
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }
        .hero-cta-btn:hover {
          transform: scale(1.045);
          box-shadow: 0 0 32px 10px color-mix(in srgb, var(--foreground) 32%, transparent), 0 4px 20px rgba(0,0,0,0.4) !important;
          animation: none;
        }
        @media (prefers-reduced-motion: no-preference) {
          @keyframes hero-btn-shimmer {
            0%   { transform: translateX(-160%) skewX(-18deg); }
            100% { transform: translateX(400%)  skewX(-18deg); }
          }
          @keyframes hero-btn-glow {
            0%, 100% { box-shadow: 0 0 0px 0px transparent, 0 2px 12px rgba(0,0,0,0.35); }
            50%       { box-shadow: 0 0 22px 6px color-mix(in srgb, var(--foreground) 22%, transparent), 0 2px 16px rgba(0,0,0,0.35); }
          }
          .hero-cta-btn {
            animation: hero-btn-glow 2.8s ease-in-out infinite;
          }
          .hero-cta-btn .shimmer-strip {
            animation: hero-btn-shimmer 2.8s ease-in-out infinite;
          }
          .hero-cta-btn:hover .shimmer-strip {
            animation: hero-btn-shimmer 1.2s ease-in-out infinite;
          }
        }
      `}</style>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* ── Left column ──────────────────────────────────────────────── */}
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial={prefersReducedMotion ? "visible" : "hidden"}
            animate="visible"
            className="flex flex-col items-start"
          >
            <motion.div variants={itemVariants} className="mb-6">
              <EyebrowBadge>Trusted by {STATS.customers} businesses worldwide</EyebrowBadge>
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-foreground mb-6 text-4xl leading-[1.08] font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              Premium Proxies. Real IPs.
              <br />
              Reliable at Scale.
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-muted-foreground mb-8 max-w-lg text-lg leading-relaxed"
            >
              {STATS.ips} ethically sourced mobile and residential proxies across {STATS.countries}{" "}
              countries. Unmatched success rates, {uptime.toFixed(3)}% uptime, and a 5-minute setup.
            </motion.p>

            <motion.div variants={itemVariants} className="mb-10 flex flex-wrap gap-3">
              <Link
                href={trialHref}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  buttonVariants({ size: "lg" }),
                  "hero-cta-btn group relative overflow-hidden font-medium"
                )}
              >
                <span
                  aria-hidden="true"
                  className="shimmer-strip pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)",
                    width: "38%",
                  }}
                />
                {isBuyMode ? "Buy Now" : FREE_TRIAL_LABEL}
                <ArrowRight
                  size={16}
                  className="ml-2 transition-transform duration-200 group-hover:translate-x-1"
                />
              </Link>
              <Link
                href={CONTACT_SALES_URL}
                className={cn(buttonVariants({ variant: "outline", size: "lg" }), "font-medium")}
              >
                Contact Sales
              </Link>
            </motion.div>

            <motion.div variants={itemVariants}>
              <TrustBenefitList className="flex flex-col gap-3 sm:flex-row sm:gap-6" />
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="border-border/50 mt-8 flex items-center gap-3 border-t pt-6"
              aria-label="Review platforms"
            >
              <ReviewBadges />
            </motion.div>
          </motion.div>

          {/* ── Right column — terminal + floating cards ─────────────────── */}
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            className="relative"
          >
            {/* Terminal */}
            <div className="border-border bg-card overflow-hidden rounded-xl border shadow-xl">
              <div className="border-border bg-muted/40 flex items-center gap-2 border-b px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="h-3 w-3 rounded-full bg-red-400/70" />
                  <div className="h-3 w-3 rounded-full bg-yellow-400/70" />
                  <div className="h-3 w-3 rounded-full bg-green-400/70" />
                </div>
                <span className="text-muted-foreground ml-2 font-mono text-xs">
                  proxidize_quickstart.py
                </span>
              </div>

              <div className="scrollbar-hide overflow-x-auto px-5 py-4 font-mono text-sm leading-[1.75]">
                {lines.map((line, lineIdx) => {
                  const show = lineIdx < visibleCount;
                  const isUsernameLine = lineIdx === 3;
                  const isLastVisible = lineIdx === visibleCount - 1;

                  const lineContent = line.map((s) => s.text).join("");
                  return (
                    <div
                      key={
                        lineContent.slice(0, 40) ||
                        (isUsernameLine ? "__empty_username__" : "__empty__")
                      }
                      className="flex items-start transition-all duration-150"
                      style={{
                        opacity: show ? 1 : 0,
                        borderRadius: 4,
                        backgroundColor:
                          isUsernameLine && flash
                            ? "color-mix(in srgb, var(--foreground) 4%, transparent)"
                            : "transparent",
                      }}
                    >
                      <span className="text-muted-foreground/30 mr-4 w-5 shrink-0 text-right text-xs leading-[1.75] select-none">
                        {lineIdx + 1}
                      </span>
                      <span className="flex flex-1 flex-wrap items-center">
                        {line.length === 0 ? (
                          <span>&nbsp;</span>
                        ) : (
                          line.map((seg) => (
                            <span key={seg.color + seg.text} style={{ color: seg.color }}>
                              {seg.text}
                            </span>
                          ))
                        )}
                        {isLastVisible && (
                          <span
                            aria-hidden="true"
                            style={{
                              display: "inline-block",
                              marginLeft: 2,
                              width: 2,
                              height: 14,
                              borderRadius: 2,
                              backgroundColor:
                                "color-mix(in srgb, var(--foreground) 15%, transparent)",
                              opacity: cursorOn ? 1 : 0,
                              transition: "opacity 0.1s",
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="border-border bg-muted/30 flex items-center gap-2 border-t px-5 py-2">
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-green-500" />
                <span className="text-muted-foreground font-mono text-xs">
                  Connected · 104.28.x.x · Mobile · US
                </span>
              </div>
            </div>

            {/* ── Floating email card ──────────────────────────────────── */}
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.2 }}
              className="bg-card border-border absolute -bottom-7 left-1/2 -translate-x-1/2 overflow-hidden rounded-xl border shadow-xl"
              style={{ width: "min(92%, 380px)" }}
            >
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-3 px-5 py-4"
                  >
                    <CheckCircle2 size={18} style={{ color: CODE_GREEN }} />
                    <div>
                      <p className="text-foreground text-sm font-semibold">Credentials sent!</p>
                      <p className="text-muted-foreground mt-0.5 text-xs">
                        Check your inbox at{" "}
                        <span className="text-muted-foreground">{username}</span>
                      </p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="px-5 py-4"
                  >
                    <p className="text-muted-foreground mb-2 text-xs font-medium">
                      Enter your email to get started
                    </p>
                    <form
                      onSubmit={formik.handleSubmit}
                      className="flex flex-col gap-1"
                      aria-label="Get your credentials"
                      noValidate
                    >
                      <div className="flex items-center gap-2">
                        <div className="relative min-w-0 flex-1">
                          <Input
                            type="email"
                            name="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={(e) => {
                              setInputFocused(false);
                              formik.handleBlur(e);
                            }}
                            onFocus={() => setInputFocused(true)}
                            placeholder=""
                            className="bg-muted/50 h-9 w-full"
                            aria-label="Your email address"
                            aria-invalid={!!(formik.touched.email && formik.errors.email)}
                          />
                          {formik.values.email === "" && !inputFocused && (
                            <span
                              aria-hidden="true"
                              className="text-muted-foreground pointer-events-none absolute inset-0 flex items-center px-3 text-sm select-none"
                            >
                              {placeholderText}
                              <span
                                style={{
                                  display: "inline-block",
                                  width: 1.5,
                                  height: 14,
                                  marginLeft: 1,
                                  borderRadius: 1,
                                  backgroundColor:
                                    "color-mix(in srgb, var(--foreground) 15%, transparent)",
                                  opacity: cursorOn ? 0.8 : 0,
                                  transition: "opacity 0.1s",
                                  verticalAlign: "middle",
                                }}
                              />
                            </span>
                          )}
                        </div>
                        <button
                          type="submit"
                          aria-label="Get credentials"
                          className={cn(
                            brandActionClasses,
                            "flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                          )}
                        >
                          <Send size={13} />
                          Get Access
                        </button>
                      </div>
                      {fieldTouchedError(formik, "email") && (
                        <FormError className="px-0.5">
                          {fieldTouchedError(formik, "email")}
                        </FormError>
                      )}
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Floating stat — Success Rate */}
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="bg-card border-border absolute -bottom-6 -left-4 rounded-lg border px-4 py-3 shadow-lg"
            >
              <div className="text-muted-foreground text-xs">Success Rate</div>
              <div className="text-foreground text-lg font-bold">99.5%</div>
            </motion.div>

            {/* Floating stat — Avg Response */}
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.85 }}
              className="bg-card border-border absolute -top-4 -right-4 rounded-lg border px-4 py-3 shadow-lg"
            >
              <div className="text-muted-foreground text-xs">Avg Response</div>
              <div className="text-muted-foreground text-lg font-bold">&lt; 0.5s</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
